import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
    Dialog, DialogContent, DialogActions, IconButton, Box,
    Typography, TextField, Button, FormControl, FormLabel,
    FormHelperText, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCommonStyles } from '../../styles/GenericStyle';
import { greyDark } from '../../colors';
import { MtComStyleObj } from '../../styles/MtComStyleObj';
import { useRegisterMaterial } from '../../api/vegApl';
import { useGlobalState } from '../../contexts/GlobalState';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { mintRegistrationStyles } from './styles';
import FullScreenActionLoader from '../common/FullScreenActionLoader';
import type { RegisterMaterialRequest } from './types';
import { convertToGrams } from '../../utils/HelperFunction';
import { useWeightScale, useScaleReadings } from '../../hooks/useWeightScale';
// import ScanInput from '../ScanInput';

const MINT_SCALE_WEIGHT_REGEX = /([0-9]+(?:[.,][0-9]+)?\s?(?:kg|g)?)/i;
// MT-SICS stable weight response, e.g. "S S      2.00033 g" (SNRU/SNR/S command replies)
const MT_SICS_STABLE_WEIGHT_REGEX = /^S\s+S\s+([-+]?[0-9]+(?:[.,][0-9]+)?)\s*(kg|g)?/i;

export interface RegisterToMintDialogProps {
    open: boolean;
    onClose: () => void;
    materialDetails: any;
    onSuccess?: () => void;
}

export function RegisterToMintDialog({ open, onClose, materialDetails, onSuccess }: RegisterToMintDialogProps) {
    const MAX_SEED_COUNT = 10000;
    const MAX_WEIGHT = 1000000;
    const classes = useCommonStyles();
    const { state: { user_id_db } } = useGlobalState();
    const { showSnackbar } = useSnackbar();
    const weightFieldRef = useRef<HTMLInputElement>(null);
    const scaleInputRef = useRef('');
    const scaleInputTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [seedCount, setSeedCount] = useState('');
    const [fieldErrors, setFieldErrors] = useState<{ weight?: string; seedCount?: string }>({});
    // const [weightIsvalid, setWeightIsvalid] = useState<boolean>(true);
    const [weight, setWeight] = useState<string>("");
    // const manualEdit = false;

    const handleWeightScan = useCallback((val: string) => {
        // Only allow digits and single decimal point
        const sanitized = val?.replace(/[^0-9.]/g, '') || "";
        
        // Prevent multiple decimal points
        const parts = sanitized.split('.');
        const normalized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : sanitized;

        // Always keep what was typed, so part-way values like "9." or "0." survive the next keystroke.
        setWeight(normalized);

        // Don't flag empty or still-being-typed values (".", "9.") as errors.
        if (normalized === '' || normalized === '.' || normalized.endsWith('.')) {
            setFieldErrors(prev => prev.weight === undefined ? prev : { ...prev, weight: undefined });
            return;
        }

        const parsedWeight = Number(normalized);
        const isValidWeight = Number.isFinite(parsedWeight) && parsedWeight > 0 && parsedWeight <= MAX_WEIGHT;

        setFieldErrors(prev => ({
            ...prev,
            weight: isValidWeight ? undefined : `Weight must be greater than 0 and up to ${MAX_WEIGHT}`
        }));
    }, []);

    // Called directly from the scale's stream loop (not a render-time effect) as new readings arrive.
    const handleScaleReading = useCallback((rawLine: string) => {
        const stableMatch = rawLine.match(MT_SICS_STABLE_WEIGHT_REGEX);
        const matchedValue = stableMatch
            ? `${stableMatch[1]}${stableMatch[2] ? ` ${stableMatch[2]}` : ''}`
            : rawLine.match(MINT_SCALE_WEIGHT_REGEX)?.[0];

        if (matchedValue) {
            const grams = convertToGrams(matchedValue);
            console.log('[scale] parsed weight:', JSON.stringify(rawLine), '->', matchedValue, '-> grams:', grams);
            handleWeightScan(grams);
        } else {
            console.log('[scale] no weight found in line:', JSON.stringify(rawLine));
        }
    }, [handleWeightScan]);

    const { isSupported: scaleSupported, connected: scaleConnected } = useWeightScale();
    useScaleReadings(open, handleScaleReading);

    useEffect(() => {
        if (!open) {
            scaleInputRef.current = '';
            if (scaleInputTimerRef.current) clearTimeout(scaleInputTimerRef.current);
            return;
        }

        const handleScaleKeyPress = (event: KeyboardEvent) => {
            // Typing in a field is already handled by its own onChange; only buffer
            // keyboard-wedge output that arrives while no field has focus.
            const activeElement = document.activeElement as HTMLElement | null;
            const isEditable = !!activeElement && (['INPUT', 'TEXTAREA', 'SELECT'].includes(activeElement.tagName) || activeElement.isContentEditable);
            if (isEditable) return;
            if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;

            scaleInputRef.current += event.key;
            if (scaleInputTimerRef.current) clearTimeout(scaleInputTimerRef.current);

            scaleInputTimerRef.current = setTimeout(() => {
                const matchedValue = scaleInputRef.current.trim().match(MINT_SCALE_WEIGHT_REGEX)?.[0];
                if (matchedValue) {
                    handleWeightScan(convertToGrams(matchedValue));
                }
                scaleInputRef.current = '';
            }, 500);
        };

        window.addEventListener('keypress', handleScaleKeyPress);
        return () => {
            window.removeEventListener('keypress', handleScaleKeyPress);
            if (scaleInputTimerRef.current) clearTimeout(scaleInputTimerRef.current);
        };
    }, [open, handleWeightScan]);

    // Derive material shape once — used by both the registration request and the display
    const { firstMaterial, nestedFirst, container, splotDetails, treatments, materialId, materialHarvestId } = useMemo(() => {
        const isArray = Array.isArray(materialDetails);
        const first = isArray ? materialDetails?.[0] : materialDetails;
        const nested = first?.[0] ?? first;
        return {
            firstMaterial: first ?? {},
            nestedFirst: nested ?? {},
            container: nested?.Containers?.[0] ?? first ?? {},
            splotDetails: first?.splotDetails ?? {},
            treatments: (nested?.Treatments || first?.Treatments || []) as any[],
            materialId: nested?.MatID || first?.matId || first?.matid || first?.material_id || '',
            materialHarvestId: first?.material_harvest_id ?? null,
        };
    }, [materialDetails]);

    const handleSeedCountChange = useCallback((rawValue: string) => {
        const sanitized = rawValue.replace(/[^0-9]/g, '');

        if (sanitized === '') {
            setSeedCount('');
            setFieldErrors(prev => prev.seedCount === undefined ? prev : { ...prev, seedCount: undefined });
            return;
        }

        // Prevent extremely large numeric strings from destabilizing numeric parsing.
        if (sanitized.length > 15) {
            setFieldErrors(prev => ({ ...prev, seedCount: `Seed count must be greater than 0 and up to ${MAX_SEED_COUNT}` }));
            return;
        }

        const numValue = Number(sanitized);
        if (!Number.isFinite(numValue) || !Number.isSafeInteger(numValue) || numValue <= 0 || numValue > MAX_SEED_COUNT) {
            setFieldErrors(prev => ({ ...prev, seedCount: `Seed count must be greater than 0 and up to ${MAX_SEED_COUNT}` }));
            return;
        }

        setSeedCount(sanitized);
        setFieldErrors(prev => prev.seedCount === undefined ? prev : { ...prev, seedCount: undefined });
    }, []);

    useEffect(() => {
        if (open) {
            setTimeout(() => {
                weightFieldRef.current?.focus();
            }, 100);
        }
    }, [open]);

    const handleClose = useCallback(() => {
        setWeight('');
        setSeedCount('');
        // setWeightIsvalid(true);
        setFieldErrors({});
        onClose();
    }, [onClose]);

    // const isSeedCountValid = Number.isSafeInteger(Number(seedCount)) && Number(seedCount) > 0 && Number(seedCount) <= MAX_SEED_COUNT;
    const isWeightValid = Number.isFinite(Number(weight)) && Number(weight) > 0 && Number(weight) <= MAX_WEIGHT;

    const { mutate: registerMaterial, isPending: isRegistering } = useRegisterMaterial({
        mutation: {
            onSuccess: (response: any) => {
                const message = response?.data?.message || 'Material registered successfully to MINT';
                showSnackbar({ message, severity: 'success' });
                handleClose();
                onSuccess?.();
            },
            onError: (error: any) => {
                const errorMessage = error?.response?.data?.detail || 'Failed to register material';
                showSnackbar({ message: errorMessage, severity: 'error' });
            }
        }
    });

    const handleConfirmRegistration = useCallback(() => {
        if (!user_id_db) {
            showSnackbar({ message: 'User session not available. Please refresh and try again.', severity: 'error' });
            return;
        }

        if (!materialDetails || (Array.isArray(materialDetails) && materialDetails.length === 0)) {
            showSnackbar({ message: 'Material details not available', severity: 'error' });
            return;
        }

        if (!container) {
            showSnackbar({ message: 'Container information not available', severity: 'error' });
            return;
        }

        const parsedSeedCount = Number(seedCount) ? Number(seedCount) : null;
        const parsedWeight = Number(weight);

        if (!Number.isFinite(parsedWeight) || parsedWeight <= 0 || parsedWeight > MAX_WEIGHT) {
            showSnackbar({ message: `Weight must be greater than 0 and up to ${MAX_WEIGHT}`, severity: 'error' });
            return;
        }

        const requestData: RegisterMaterialRequest = {
            user_id: user_id_db,
            container: {
                registered_date: new Date().toISOString(),
                registered_by: user_id_db,
                registered_count: parsedSeedCount,
                registered_grams: parsedWeight,
                created_by: user_id_db,
                created_date: new Date().toISOString(),
                no_seeds: container.no_seeds || false,
                material_container_id: container.material_container_id || firstMaterial.material_container_id || 0,
                material_id: container.material_id || nestedFirst?.material_id || 0,
                container_uuid: container.container_uuid || firstMaterial.container_uuid || '',
                container_alt_id: container.container_alt_id || firstMaterial.container_alt_id || ''
            },
            treatment_uuids: treatments.map((t: any) => t.treatment_uuid).filter(Boolean),
            seed_processing_lot_id: nestedFirst?.splot_id || firstMaterial.splot_id || splotDetails.splot_id || firstMaterial.seed_processing_lot_id || 0,
            location_uuid: nestedFirst?.location_uuid || splotDetails.location_uuid || firstMaterial.location_uuid || '',
            crop_xrd_oid: nestedFirst?.crop_xrd_oid || splotDetails.crop_xrd_oid || firstMaterial.crop_xrd_oid || '',
            batch_uuid: nestedFirst?.batch_uuid || splotDetails.batch_uuid || firstMaterial.batch_uuid || '',
            mat_id: materialId,
            material_harvest_id: materialHarvestId ?? null,
        };

        registerMaterial({ data: requestData });
    }, [materialDetails, weight, seedCount, user_id_db, registerMaterial, showSnackbar, firstMaterial, nestedFirst, container, splotDetails, treatments, materialId, materialHarvestId]);

    return (
        <>
        <FullScreenActionLoader open={isRegistering} />
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
            slotProps={{ paper: { sx: mintRegistrationStyles.dialogPaper } }}>
            <IconButton aria-label="close" onClick={handleClose}
                sx={(theme) => ({
                    ...MtComStyleObj(theme).dialogCloseButton,
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    zIndex: 1
                })}>
                <CloseIcon />
            </IconButton>

            <DialogContent sx={{ pt: 2 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" className={classes.labelNoto16Bold700} sx={{ mb: 0.5 }}>
                        Material ID : {materialId}
                    </Typography>
                </Box>

                <FormControl fullWidth sx={{ mb: 2 }} error={!!fieldErrors.weight}>
                    <FormLabel sx={{ color: greyDark, mb: 1 }} className={classes.labelInter14Bold400}>
                        Seed Weight (g) <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                    </FormLabel>


                    {/* <ScanInput submitOnScan
                        scanPickupEnabled={true}
                        manualEdit={manualEdit}
                        customParser={(val) => convertToGrams(val.replace(/[^0-9.]/g, ''))}
                        error={!weightIsvalid && Boolean(weight)}
                        label={'Weight in grams'}
                        id="weight-in-grams"
                        scanRegex={/[0-9.]/}
                        manualInputDelay={250}                      
                        onScan={handleWeightScan}
                        onChange={handleWeightScan}
                        onKeyDown={(e) => {
                            // Allow navigation and control keys
                            const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
                            if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
                                return;
                            }
                            // Only allow digits and decimal point
                            if (!/^[0-9.]$/.test(e.key)) {
                                e.preventDefault();
                                return;
                            }
                            // Prevent second decimal point
                            const target = e.target as HTMLInputElement;
                            if (e.key === '.' && target.value.includes('.')) {
                                e.preventDefault();
                            }
                        }}
                        placeholder={"Enter or Scan Weight"} /> */}
                    <TextField
                        fullWidth
                        type="text"
                        inputRef={weightFieldRef}
                        value={weight}
                        placeholder="Enter Weight"
                        size="small"
                        onChange={(e) => handleWeightScan(convertToGrams(e.target.value))}
                        sx={(theme) => ({
                            ...MtComStyleObj(theme).textFocusHoverStyle,
                            ...MtComStyleObj(theme).textBoxValueStyle,
                        })}
                        slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                    />
                    {fieldErrors.weight && <FormHelperText>{fieldErrors.weight}</FormHelperText>}
                    {scaleSupported && !scaleConnected && (
                        <FormHelperText sx={{ ml: 0, color: '#ED6C02' }}>
                            Scale not connected. Connect it from the menu to auto-fill the weight, or type it manually.
                        </FormHelperText>
                    )}
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }} error={!!fieldErrors.seedCount}>
                    <FormLabel sx={{ color: greyDark, mb: 1 }} className={classes.labelInter14Bold400}>
                        Seed Count
                    </FormLabel>
                    <TextField fullWidth type="text" value={seedCount} placeholder="Enter seed count" size="small"
                        inputMode="numeric"
                        onKeyDown={(e) => { if (e.key.length === 1 && !/^[0-9]$/.test(e.key)) e.preventDefault(); }}
                        onChange={(e) => handleSeedCountChange(e.target.value)}
                        sx={(theme) => ({
                            ...MtComStyleObj(theme).textFocusHoverStyle,
                            ...MtComStyleObj(theme).textBoxValueStyle,
                        })} slotProps={{ htmlInput: { min: 0, max: MAX_SEED_COUNT, pattern: '[0-9]*' } }} />
                    {fieldErrors.seedCount && <FormHelperText>{fieldErrors.seedCount}</FormHelperText>}
                </FormControl>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="contained" onClick={handleConfirmRegistration} className={classes.greenButton}
                    fullWidth sx={mintRegistrationStyles.confirmButton}
                    disabled={isRegistering || !isWeightValid || !!fieldErrors.weight || !!fieldErrors.seedCount}>
                    {isRegistering ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Confirm and Register to MINT'}
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
}
